import type { Geometry } from 'geojson';
import { appConfig } from '@/config/app';
import type { CountyFeatureProps, GeographyIndex, NeighborhoodPoint, ZipFeatureProps } from '@/types/geography';
import { TEXAS_CITIES } from './texasCounties';

export interface CountyGeoJson {
  type: 'FeatureCollection';
  properties?: { _placeholder?: boolean; _note?: string };
  features: Array<{
    type: 'Feature';
    properties: CountyFeatureProps;
    geometry: Geometry;
  }>;
}

export interface ZipGeoJson {
  type: 'FeatureCollection';
  properties?: { _placeholder?: boolean; _note?: string };
  features: Array<{
    type: 'Feature';
    properties: ZipFeatureProps;
    geometry: Geometry;
  }>;
}

let cachedGeoJson: CountyGeoJson | null = null;
let cachedIndex: GeographyIndex | null = null;
let cachedZipGeoJson: ZipGeoJson | null = null;

/**
 * Loads public/geo/texas-counties.geojson (same-origin, no backend).
 * Ships with real county boundary polygons for all 254 Texas counties,
 * sourced from topojson/us-atlas's counties-10m.json (Census Bureau
 * cartographic county boundaries, 1:10,000,000 generalized). See the
 * FeatureCollection's top-level `properties._note` for exact provenance.
 * This replaced an earlier 1:20,000,000-generalized TIGERweb source that
 * was too coarse at county-level map zoom (some counties rendered as
 * blocky near-rectangles, most noticeably on hover). For even higher
 * fidelity (1:500,000), run scripts/fetch-counties-500k.mjs on a machine
 * with normal internet access -- see that script's header comment. The old
 * placeholder-rectangle set (see scripts/generate-placeholder-geojson.mjs,
 * now superseded) is no longer used. The rest of the app does not care
 * about geometry fidelity beyond visual quality, only about the `geoId`
 * used to join to market data.
 */
export async function loadCountyGeoJson(signal?: AbortSignal): Promise<CountyGeoJson> {
  if (cachedGeoJson) return cachedGeoJson;
  const res = await fetch(`${appConfig.geoBasePath}/texas-counties.geojson`, { signal });
  if (!res.ok) throw new Error(`loadCountyGeoJson: fetch failed (${res.status})`);
  cachedGeoJson = (await res.json()) as CountyGeoJson;
  return cachedGeoJson;
}

export async function loadGeographyIndex(signal?: AbortSignal): Promise<GeographyIndex> {
  if (cachedIndex) return cachedIndex;
  const geoJson = await loadCountyGeoJson(signal);

  const countiesById = new Map<string, CountyFeatureProps>();
  const countyNameToId = new Map<string, string>();
  for (const feature of geoJson.features) {
    const props = feature.properties;
    countiesById.set(props.geoId, props);
    countyNameToId.set(props.name.toLowerCase(), props.geoId);
  }

  const citiesById = new Map(TEXAS_CITIES.map((c) => [c.geoId, c]));
  const cityNameToId = new Map(TEXAS_CITIES.map((c) => [c.name.toLowerCase(), c.geoId]));

  cachedIndex = { countiesById, citiesById, countyNameToId, cityNameToId };
  return cachedIndex;
}

export function isPlaceholderGeometry(geoJson: CountyGeoJson): boolean {
  return Boolean(geoJson.properties?._placeholder);
}

/**
 * Loads public/geo/texas-zips.geojson (same-origin, no backend) -- the ZIP
 * layer's boundary counterpart to loadCountyGeoJson above. Ships as a
 * placeholder ({ features: [] }, properties._placeholder: true) until
 * scripts/fetch-zips-500k.mjs is run (see that script's header comment for
 * why it can't be run from this environment) to populate real ZCTA
 * boundaries from Census TIGERweb. ZipLayer.tsx checks isPlaceholderGeometry
 * on this result the same way county code does, so the UI can say "ZIP
 * boundaries not loaded yet" honestly instead of silently rendering nothing.
 */
export async function loadZipGeoJson(signal?: AbortSignal): Promise<ZipGeoJson> {
  if (cachedZipGeoJson) return cachedZipGeoJson;
  const res = await fetch(`${appConfig.geoBasePath}/texas-zips.geojson`, { signal });
  if (!res.ok) throw new Error(`loadZipGeoJson: fetch failed (${res.status})`);
  cachedZipGeoJson = (await res.json()) as ZipGeoJson;
  return cachedZipGeoJson;
}

export function isPlaceholderZipGeometry(geoJson: ZipGeoJson): boolean {
  return Boolean(geoJson.properties?._placeholder);
}

interface NeighborhoodPointsFile {
  _placeholder?: boolean;
  _note?: string;
  points: NeighborhoodPoint[];
}

let cachedNeighborhoodPoints: NeighborhoodPoint[] | null = null;
let cachedNeighborhoodPlaceholder = true;

/**
 * Loads public/geo/texas-neighborhoods.json -- point-marker locations
 * (name + city + lat/lon) for named neighborhoods in the four target metro
 * cores. Ships as a placeholder (`{ _placeholder: true, points: [] }`)
 * until scripts/fetch-neighborhoods-osm.mjs is run to populate it from
 * OpenStreetMap. No real neighborhood *polygon* boundaries are used
 * anywhere in this app (OSM's neighborhood-boundary coverage is too
 * inconsistent city-to-city to trust for a choropleth) -- NeighborhoodLayer
 * intentionally renders point markers only, the same honest simplification
 * CityLayer already uses for cities.
 */
export async function loadNeighborhoodPoints(signal?: AbortSignal): Promise<NeighborhoodPoint[]> {
  if (cachedNeighborhoodPoints) return cachedNeighborhoodPoints;
  const res = await fetch(`${appConfig.geoBasePath}/texas-neighborhoods.json`, { signal });
  if (!res.ok) throw new Error(`loadNeighborhoodPoints: fetch failed (${res.status})`);
  const data = (await res.json()) as NeighborhoodPointsFile;
  cachedNeighborhoodPlaceholder = Boolean(data._placeholder);
  cachedNeighborhoodPoints = data.points || [];
  return cachedNeighborhoodPoints;
}

export function isPlaceholderNeighborhoodPoints(): boolean {
  return cachedNeighborhoodPlaceholder;
}
